import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../../../redux/useDispatch";
import {
  setSelectedLocation,
  setHasSelectedLocation,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { setSelectedLocation as setSubscriptionSelectedLocation } from "../../../../redux/slice/subscription/subscriptionSlice";
import { useKeyHandoverLocations } from "../context/KeyHandoverContext";
import { parseBookingQuery } from "../common/url/bookingQuery";
import type { RootState } from "../../../../redux/store";

/**
 * Custom hook to hydrate location selection from URL query parameters.
 *
 * @remarks
 * - Must be called within KeyHandoverProvider context
 * - Only runs once per mount
 * - Updates Redux store with location selection
 * - Returns true when hydration is complete
 *
 * @example
 * ```tsx
 * function Page() {
 *   const locationHydrated = useLocationHydration();
 *   if (!locationHydrated) return <Loading />;
 *   return <Content />;
 * }
 * ```
 */
export function useLocationHydration(): boolean {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const locations = useKeyHandoverLocations();
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const subscriptionSelectedLocation = useSelector(
    (state: RootState) => state.subscription.newSubscription.selectedLocation
  );

  const hydratedRef = useRef(false);
  const capturedRef = useRef(false);
  const initialLocationIdRef = useRef<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Capture initial URL parameters once on mount
  useEffect(() => {
    if (capturedRef.current) return;

    const query = parseBookingQuery(location.search);
    initialLocationIdRef.current = query.locationId ?? null;
    capturedRef.current = true;
  }, [location.search]);

  // Hydrate from captured URL parameters
  useEffect(() => {
    if (hydratedRef.current) return;

    const locId = initialLocationIdRef.current;

    // If no locationId in URL, mark as hydrated immediately
    if (!locId) {
      hydratedRef.current = true;
      setIsHydrated(true);
      return;
    }

    // If location already matches the URL param, mark as hydrated immediately
    if (selectedLocation && selectedLocation.id === locId) {
      // Ensure subscription slice also has the hydrated location
      if (!subscriptionSelectedLocation || subscriptionSelectedLocation.id !== locId) {
        dispatch(setSubscriptionSelectedLocation(selectedLocation));
      }
      hydratedRef.current = true;
      setIsHydrated(true);
      return;
    }

    // Otherwise, wait for locations to load
    if (!locations || locations.length === 0) return;

    // Try to find and set the location
    const found = locations.find((l) => l.id === locId);
    if (found) {
      dispatch(setSelectedLocation(found));
      dispatch(setSubscriptionSelectedLocation(found));
      dispatch(setHasSelectedLocation(true));
    } else {
      console.warn(`[useLocationHydration] Location with id ${locId} not found in available locations`);
    }

    hydratedRef.current = true;
    setIsHydrated(true);
  }, [locations, selectedLocation, subscriptionSelectedLocation, dispatch]);

  return isHydrated;
}
